import { useState, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Pill, EmptyState, showToast } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import {
  useAgentRoutines,
  useUpdateRoutine,
  useArchiveRoutine,
  useRestoreRoutine,
  useDeleteRoutine,
} from "../../hooks/use-routines";
import { useIsOwner } from "../../hooks/use-is-owner";
import { PanelRowMenu } from "./PanelRowMenu";
import { ConfirmDeleteRowModal } from "./ConfirmDeleteRowModal";
import { ArchivedBadge, ShowArchivedChip, useShowArchived } from "./panel-archive";
import { NewRoutineModal } from "../scope/NewRoutineModal";
import { formatRelativeTime } from "../../lib/v3-columns";
import type { Routine } from "../../../lib/types";

interface RoutinesPanelProps {
  nodeId: string;
  actionsBelowTitle?: boolean;
}

/**
 * Sprint 14 — an agent's declared Routines (DOES). Each row toggles enabled,
 * opens the full RoutineView on click, and offers "Run now" which copies a
 * host-ready prompt to the clipboard (Kuvio never executes — the host does).
 *
 * Sprint 14g — reference implementation of the unified archive/delete pattern:
 * a "Show archived" chip, a per-row ⋮ menu (Archive / Restore / owner-only
 * Delete), an undo toast on archive, and a type-the-name delete modal.
 */
export function RoutinesPanel({ nodeId, actionsBelowTitle }: RoutinesPanelProps) {
  const [showArchived, setShowArchived] = useShowArchived("routines", nodeId);
  const routinesQuery = useAgentRoutines(nodeId, undefined, showArchived);
  const [newOpen, setNewOpen] = useState(false);

  const routines = routinesQuery.data ?? [];

  const headerActions = (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <ShowArchivedChip value={showArchived} onChange={setShowArchived} />
      <button onClick={() => setNewOpen(true)} style={headerBtnStyle} title="Declare a routine">
        <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> New routine
      </button>
    </div>
  );

  return (
    <>
      <Panel
        title="Routines"
        count={routines.length}
        action={headerActions}
        actionsBelowTitle={actionsBelowTitle}
        isLoading={routinesQuery.isLoading}
        error={routinesQuery.error}
        onRetry={() => routinesQuery.refetch()}
        isEmpty={routines.length === 0}
        empty={
          showArchived ? (
            <EmptyState>No archived items. Items you archive will appear here for restore.</EmptyState>
          ) : (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <EmptyState>No routines for this agent yet.</EmptyState>
              <button onClick={() => setNewOpen(true)} data-variant="primary" style={{ fontSize: 13, padding: "6px 12px", marginTop: 8 }}>
                <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" /> New routine
              </button>
            </div>
          )
        }
      >
        {routines.map((r) => (
          <RoutineRow key={r.id} routine={r} />
        ))}
      </Panel>

      <NewRoutineModal open={newOpen} onClose={() => setNewOpen(false)} agentId={nodeId} />
    </>
  );
}

export function RoutineRow({ routine }: { routine: Routine }) {
  const navigate = useNavigate();
  const updateRoutine = useUpdateRoutine();
  const archiveRoutine = useArchiveRoutine();
  const restoreRoutine = useRestoreRoutine();
  const deleteRoutine = useDeleteRoutine();
  const canDelete = useIsOwner();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isArchived = !!routine.archived_at;

  function toggleEnabled(e: React.MouseEvent) {
    e.stopPropagation();
    updateRoutine.mutate({ id: routine.id, enabled: !routine.enabled });
  }

  function runNow(e: React.MouseEvent) {
    e.stopPropagation();
    const body = routine.prompt_body ?? `(Shared prompt ${routine.prompt_ref ?? ""} — fetch with get_routine)`;
    const payload = `# Routine: ${routine.name}\n# Purpose: ${routine.purpose}\n\n${body}`;
    navigator.clipboard?.writeText(payload).then(
      () => showToast("Host-ready prompt copied — run it in your host, then record_routine_run"),
      () => showToast("Couldn't copy to clipboard", "error", true),
    );
  }

  function handleArchive() {
    archiveRoutine.mutate(routine.id, {
      onSuccess: () =>
        showToast(`Archived '${routine.name}'`, "success", false, {
          label: "Undo",
          onClick: () => restoreRoutine.mutate(routine.id),
        }, 10000),
      onError: (e) => showToast(e instanceof Error ? e.message : "Archive failed", "error", true),
    });
  }

  function handleRestore() {
    restoreRoutine.mutate(routine.id, {
      onSuccess: () => showToast(`Restored '${routine.name}'`),
      onError: (e) => showToast(e instanceof Error ? e.message : "Restore failed", "error", true),
    });
  }

  const lastRun = routine.last_run_at
    ? `Last run: ${formatRelativeTime(routine.last_run_at)}${routine.last_run_by ? ` by ${routine.last_run_by}` : ""}${routine.last_run_summary ? ` — "${routine.last_run_summary}"` : ""}`
    : "Last run: never";

  return (
    <>
      <PanelRow onClick={() => navigate(`/routines/${routine.id}`)} title={routine.name}>
        <button
          type="button"
          onClick={toggleEnabled}
          aria-label={routine.enabled ? "Disable routine" : "Enable routine"}
          title={routine.enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
          style={toggleStyle(routine.enabled)}
        >
          {routine.enabled && <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" />}
        </button>
        <div style={{ flex: 1, minWidth: 0, opacity: isArchived ? 0.6 : 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={nameStyle(routine.enabled)}>{routine.name}</span>
            <Pill variant="gray">{routine.cadence}</Pill>
            {isArchived && <ArchivedBadge />}
          </div>
          <div style={subStyle}>{routine.purpose}</div>
          <div style={metaStyle}>{lastRun}</div>
        </div>
        {!isArchived && (
          <button type="button" onClick={runNow} style={runBtnStyle} title="Copy a host-ready prompt to run this routine">
            <i className="ti ti-player-play" style={{ fontSize: 12 }} aria-hidden="true" /> Run now
          </button>
        )}
        <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", alignSelf: "flex-start" }}>
          <PanelRowMenu
            isArchived={isArchived}
            canDelete={canDelete}
            onArchive={handleArchive}
            onRestore={handleRestore}
            onDelete={() => setConfirmDelete(true)}
          />
        </span>
      </PanelRow>

      <ConfirmDeleteRowModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        entityKind="routine"
        entityName={routine.name}
        entityId={routine.id}
        onConfirm={async ({ force }) => {
          await deleteRoutine.mutateAsync({ id: routine.id, force });
          showToast(`Deleted '${routine.name}'`);
        }}
      />
    </>
  );
}

const headerBtnStyle: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const runBtnStyle: CSSProperties = {
  ...headerBtnStyle,
  flexShrink: 0,
  alignSelf: "flex-start",
};

const toggleStyle = (on: boolean): CSSProperties => ({
  width: 18,
  height: 18,
  flexShrink: 0,
  alignSelf: "flex-start",
  marginTop: 2,
  borderRadius: 4,
  border: on ? "1px solid var(--c-teal-600)" : "1px solid var(--color-border-secondary)",
  background: on ? "var(--c-teal-600)" : "transparent",
  color: "#fff",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
});

const nameStyle = (enabled: boolean): CSSProperties => ({
  fontSize: 13,
  fontWeight: 500,
  color: enabled ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
});

const subStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const metaStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
